import { FC, useState, useCallback, useContext, useRef } from 'react';
import Button from '../../ui/Button/Button';
import { Form } from 'ui/FormContainer/FormContainer';
import FormField from '../../ui/FormField/FormField';
import withContainer from '../../components/Container/withContainer';
import { AuthorizeByCodeData } from 'api/auth/auth.types';
import ButtonsContainer from 'ui/Button/ButtonContainer';
import { sendRecoveryCode, authByCode } from 'api/auth/auth';
import { useNavigate } from 'react-router-dom';
import { RouterPaths } from 'helpers/router-paths';
import { AuthContext } from 'context/Auth';
import { RawLink } from 'ui/Link';
import { ButtonsField } from 'ui/ButtonsField/ButtonsField';
import { FormRenderProps, useField, useForm } from 'react-final-form';
import { FormApi } from 'final-form';
import useUpdateEffect from 'hooks/useUpdateEffect';
import { FormRow } from 'ui/Form/Form.styled';
import { FieldButton } from 'components/fields/PhotoField/PhotoField.styled';

type Data = AuthorizeByCodeData;

const Field = FormField<Data>;

const SEND_CODE_INTERVAL = 60;

const STORAGE_KEY = 'password_recovery_state';

type LoginType = 'email' | 'phone';

type FormValues = Data & {
    isCodeSent: boolean;
    loginType?: LoginType;
    codeAvailableAt?: Date;
};

const SwitchLoginTypeButton = () => {
    const field = useField<LoginType>('loginType' satisfies keyof FormValues);
    const form = useForm();
    const handleClick = useCallback(() => {
        field.input.onChange(field.input.value === 'email' ? 'phone' : 'email');
        form.change('isCodeSent', false);
        form.change('codeAvailableAt', undefined);
    }, [field.input, form]);

    return (
        <Button onClick={handleClick}>
            Я не помню {field.input.value === 'email' ? 'E-mail' : 'телефон'}
        </Button>
    );
};

type SendCodeFunction = (
    props: FormValues,
    form: FormApi<FormValues>,
    callback?: (codeAvailableAt: Date) => void
) => void;

type RecoveryFormRenderProps = FormRenderProps<FormValues> & {
    sendCode: SendCodeFunction;
};

const FormRender: FC<RecoveryFormRenderProps> = ({
    sendCode,
    submitting,
    pristine,
    values,
    form
}) => {
    const { loginType, isCodeSent, codeAvailableAt } = values;
    const [isSending, setIsSending] = useState(false);

    const timerRef = useRef<NodeJS.Timeout>();

    useUpdateEffect(() => {
        if (loginType) {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(values));
        }
    }, [loginType]);

    const [secondsToCode, setSecondsToCode] = useState<number>(() => {
        if (codeAvailableAt) {
            return Math.ceil(
                (new Date(codeAvailableAt).getTime() - Date.now()) / 1000
            );
        }
        return SEND_CODE_INTERVAL;
    });

    const startTimer = useCallback((codeAvailableAt: Date) => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
        }

        if (codeAvailableAt) {
            const codeAvailableAtTime = new Date(codeAvailableAt).getTime();
            setSecondsToCode(
                Math.max(
                    0,
                    Math.ceil((codeAvailableAtTime - Date.now()) / 1000)
                )
            );

            // console.log(codeAvailableAtTime);

            timerRef.current = setInterval(() => {
                setSecondsToCode((prev) => {
                    if (prev <= 0) {
                        clearInterval(timerRef.current);
                        return 0;
                    }

                    return Math.ceil((codeAvailableAtTime - Date.now()) / 1000);
                });
            }, 1000);
        }

        return () => timerRef.current && clearInterval(timerRef.current);
    }, []);

    useUpdateEffect(() => {
        if (codeAvailableAt) {
            startTimer(codeAvailableAt);
        }
    }, [codeAvailableAt]);

    const handleSendCode = useCallback(() => {
        setIsSending(true);
        sendCode(values, form, (codeAvailableAt) => {
            setSecondsToCode(SEND_CODE_INTERVAL);
            startTimer(codeAvailableAt);
            setIsSending(false);
        });
    }, [sendCode, values, form, startTimer]);

    const enableEditMode = useCallback(() => {
        form.change('isCodeSent', false);
    }, [form]);

    if (loginType) {
        return (
            <>
                <FormRow>
                    <Field
                        name={loginType}
                        label={loginType === 'email' ? 'E-mail' : 'Телефон'}
                        type={loginType === 'email' ? 'email' : 'tel'}
                        required
                        disabled={isCodeSent}
                    />
                    {isCodeSent && (
                        <FieldButton onClick={enableEditMode}>
                            Изменить
                        </FieldButton>
                    )}
                </FormRow>
                {isCodeSent ? (
                    <>
                        <Field
                            name="code"
                            label="Код"
                            type="number"
                            required
                            hideMeta
                        />
                        <RawLink
                            disabled={secondsToCode > 0 || isSending}
                            onClick={handleSendCode}
                        >
                            {isSending
                                ? 'Отправляем код...'
                                : `Запросить новый код
                    ${secondsToCode > 0 ? ` через ${secondsToCode} секунд` : ''}`}
                        </RawLink>
                        <ButtonsContainer>
                            <Button
                                type="submit"
                                disabled={submitting || pristine}
                            >
                                Войти в систему
                            </Button>
                            <SwitchLoginTypeButton />
                        </ButtonsContainer>
                    </>
                ) : (
                    <>
                        <ButtonsContainer>
                            <Button type="submit" disabled={submitting}>
                                Запросить код
                            </Button>
                            <SwitchLoginTypeButton />
                        </ButtonsContainer>
                    </>
                )}
            </>
        );
    }

    return (
        <ButtonsField<LoginType>
            name="loginType"
            shouldSubmit={false}
            options={[
                {
                    value: 'email',
                    children: 'Я помню E-mail'
                },
                {
                    value: 'phone',
                    children: 'Я помню телефон'
                }
            ]}
        />
    );
};

const PasswordRecovery: FC = () => {
    const [initialValues] = useState<FormValues>(() => {
        const savedValues = sessionStorage.getItem(STORAGE_KEY);
        return savedValues ? JSON.parse(savedValues) : {};
    });
    const navigate = useNavigate();

    const { refresh } = useContext(AuthContext);

    const saveFormValues = useCallback((values: FormValues | null) => {
        if (values) {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(values));
        } else {
            sessionStorage.removeItem(STORAGE_KEY);
        }
    }, []);

    const sendCode = useCallback<SendCodeFunction>(
        (data, form, callback) => {
            const newData = { ...data };

            if (data.loginType === 'email') {
                delete newData.phone;
            } else {
                delete newData.email;
            }

            sendRecoveryCode(newData)(async () => {
                const codeAvailableAt = new Date(
                    Date.now() + SEND_CODE_INTERVAL * 1000
                );

                form.change('isCodeSent', true);
                form.change('codeAvailableAt', codeAvailableAt);
                saveFormValues(form.getState().values);
                callback?.(codeAvailableAt);
            });
        },
        [saveFormValues]
    );

    const recoveryPassword = useCallback(
        (data: FormValues, form: FormApi<FormValues>) => {
            if (!data.isCodeSent) {
                sendCode(data, form);
            } else {
                authByCode(data)(async () => {
                    await refresh();

                    saveFormValues(null);
                    sessionStorage.removeItem(STORAGE_KEY);
                    navigate(RouterPaths.SetPassword, {
                        unstable_viewTransition: true
                    });
                });
            }
        },
        [navigate, refresh, sendCode, saveFormValues]
    );

    return (
        <Form<FormValues>
            onSubmit={recoveryPassword}
            initialValues={initialValues}
            render={(props) => <FormRender {...props} sendCode={sendCode} />}
        />
    );
};

export default withContainer(PasswordRecovery, {
    heading: 'Восстановление пароля'
});
